import abbreviateNumber from 'helpers/abbreviateNumber';

const MAX_INT = 2147483647;
const PERC_TREND_ABBREV_THRESHOLD = 99.99;

export type FormatType =
  | 'trend'
  | 'percentage'
  | 'number'
  | 'position'
  | 'change'
  | 'full';

interface IFormatingOptions {
  style?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits: number;
}

/** Number Formatting helper. */
const getFormattingOptions = (type: FormatType): IFormatingOptions => {
  switch (type) {
    case 'trend':
    case 'percentage':
      return {
        style: 'percent',
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      };
    case 'number':
    default:
      return {
        maximumFractionDigits: 2,
      };
  }
};

/**
 * This helper formats numbers to human readable strings in their locale.
 * The values can optionally be abbreviated.
 *
 * @export
 * @param {number | null} value The number to format
 * @param {('number'|'trend'|'position'|'change'|'percentage'|'full')} [type='number'] The type of number
 * @param {boolean} [abbreviate=false] Should this value be abbreviated?
 * @param {string} [locale='en-US'] The locale to format numbers for
 * @returns
 */
export default function formatNumber(
  value: number | null,
  type: FormatType = 'number',
  abbreviate = false,
  locale = 'en-US'
): string {
  const options = getFormattingOptions(type);
  let number: string | number | null = value;
  let prefix = '';

  // If the value is null we want to show an en-dash
  if (typeof number !== 'number' || isNaN(number)) {
    return '–';
  }

  // Round regular numbers to integers
  if (type === 'number') {
    const rounded = Math.round(number);

    // But only if the number isn't 0 when rounded and wasn't 0 before
    if (!(rounded === 0 && number !== 0)) {
      number = rounded;
    }
  }

  if (type === 'full') {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // Prefix a + for percentages if the value is higher than 0
  if (type === 'trend' && number >= 0) {
    prefix = '+';
  }

  if (type === 'position') {
    return `#${number}`;
  }

  // Absolute change
  if (type === 'change') {
    if (number === 0) {
      return '–';
    }

    if (number > 0) {
      prefix = '+';
    }
  }

  /** If number is an infinite number display the infinite sign.
   *  if the number is bigger than 9999% always abbreviate.
   **/
  if (type === 'percentage' || type === 'trend') {
    if (number >= MAX_INT) {
      return '∞';
    } else if (number > PERC_TREND_ABBREV_THRESHOLD) {
      abbreviate = true;
    }
  }

  if (abbreviate) {
    if (type === 'percentage' || type === 'trend') {
      /**
       * Multiply the number by 100 to make it a percentage value. For non-abbreviated numbers this is done by toLocaleString()
       * Call toFixed to make sure the number has a maximum of of 1 decimal.
       * */
      number = number * 100;

      if (number >= 100) {
        number = Math.round(number);
      } else {
        number = number.toFixed(1);
      }

      number = abbreviateNumber(number);
      number = number + '%';
    } else {
      number = abbreviateNumber(number);
    }

    return prefix + number;
  }

  return prefix + number.toLocaleString(locale, options);
}
